import type { QuoteLine } from '../api/types'
import { isWholeQuantity } from '../ui/numbers'
import type { InstallationEstimateInput } from './services-api'

export const round = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100
const decimal = /^\d+(\.\d+)?$/

/** Unit Net, unit sell and the extended totals for one decision, as the server will price it. */
export function servicePricing(draft: InstallationEstimateInput, line?: QuoteLine) {
  const unitCost = Number(draft.net_override ?? round(Number(draft.crew_hours || 0) * Number(draft.hourly_rate || 0)))
  const oldCost = line ? Number(line.derived_net_amount ?? line.source_net_amount ?? line.list_amount) : 0
  const oldSell = line ? Number(line.sell_amount ?? line.list_amount) : 0
  const unitSell =
    draft.markup_percent != null
      ? round(unitCost * (1 + Number(draft.markup_percent) / 100))
      : line
        ? Math.max(round(oldCost ? (unitCost * oldSell) / oldCost : oldSell), Number(line.umrp_amount ?? 0))
        : round(unitCost / 0.9)
  const quantity = draft.included ? Number(draft.quantity || 0) : 0
  const cost = round(unitCost * quantity)
  const sell = round(unitSell * quantity)
  return { cost, sell, profit: round(sell - cost), unitSell }
}

export function serviceIntelligence(drafts: InstallationEstimateInput[]) {
  const hours = drafts.reduce((sum, row) => sum + (row.included ? Number(row.crew_hours) * Number(row.quantity) : 0), 0)
  const estimate = drafts.reduce(
    (sum, row) => sum + (row.included ? Number(row.crew_hours) * Number(row.quantity) * Number(row.hourly_rate) : 0),
    0,
  )
  return {
    hours,
    rate: hours ? estimate / hours : 0,
    estimate: round(estimate),
  }
}

/** Spread a typed installation Net across the included rows by each row's share of the current Net. */
export function allocateServiceNet(drafts: InstallationEstimateInput[], total: number) {
  const included = drafts
    .filter((row) => row.included)
    .sort((a, b) => a.equipment_line_id.localeCompare(b.equipment_line_id))
  const cost = included.reduce((sum, row) => sum + servicePricing(row).cost, 0)
  const weight = (row: InstallationEstimateInput) => (cost > 0 ? servicePricing(row).cost : Number(row.quantity))
  const totalWeight = included.reduce((sum, row) => sum + weight(row), 0)
  let cumulative = 0
  let previous = 0
  const allocations = new Map(
    included.map((row) => {
      cumulative += weight(row)
      const through = round((total * cumulative) / totalWeight)
      const net = ((through - previous) / Number(row.quantity)).toFixed(7)
      previous = through
      return [row.equipment_line_id, net] as const
    }),
  )
  return drafts.map((row) =>
    row.included
      ? {
          ...row,
          net_override: allocations.get(row.equipment_line_id) ?? row.net_override,
        }
      : row,
  )
}

/** Spread a typed installation sell the same way, as each row's markup over its own Net. */
export function allocateServiceSell(drafts: InstallationEstimateInput[], total: number) {
  const allocation = allocateServiceNet(drafts, total)
  return drafts.map((row, index) => {
    const net = Number(row.net_override ?? round(Number(row.crew_hours) * Number(row.hourly_rate)))
    return row.included && net > 0
      ? {
          ...row,
          markup_percent: String((round(Number(allocation[index]?.net_override)) / net - 1) * 100),
        }
      : row
  })
}

export function draftIsInvalid(draft: InstallationEstimateInput) {
  return (
    ![draft.crew_hours, draft.hourly_rate].every((value) => decimal.test(value)) ||
    !isWholeQuantity(draft.quantity) ||
    Number(draft.crew_hours) <= 0 ||
    (draft.net_override != null && (!decimal.test(draft.net_override) || Number(draft.net_override) < 0)) ||
    (draft.markup_percent != null &&
      (draft.markup_percent === '' ||
        !Number.isFinite(Number(draft.markup_percent)) ||
        Number(draft.markup_percent) < -100 ||
        Number(draft.markup_percent) > 10000))
  )
}
